import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateMemberRole as updateMemberRoleApi } from "../api/account";
import { useHostToast } from "../../host/useHostToast";

interface UseUpdateMemberRole {
  /** Member id whose role is currently being updated, or null. The
   *  table uses this for per-row spinner UX — drives the same affordance
   *  the original `MemberRoleSelectionV2.isUpdatingRole` provides. */
  pendingMemberId: string | null;
  update: (accountId: string, userId: string, role: string) => Promise<boolean>;
}

export function useUpdateMemberRole(): UseUpdateMemberRole {
  const { t } = useTranslation();
  const toast = useHostToast();
  const [pendingMemberId, setPendingMemberId] = useState<string | null>(null);

  const update = useCallback(
    async (accountId: string, userId: string, role: string) => {
      setPendingMemberId(userId);
      try {
        await updateMemberRoleApi(accountId, userId, role);
        toast.success(t("team.toast.roleUpdated"));
        return true;
      } catch {
        toast.error(t("team.toast.roleUpdateFailed"));
        return false;
      } finally {
        setPendingMemberId(null);
      }
    },
    [t, toast],
  );

  return { update, pendingMemberId };
}
